import Matter from 'matter-js';
import { Sprite, type Texture } from 'pixi.js';
import 'pathseg';
import {
  calculateOpaqueCenterOfMass,
  createTextureCanvasContext,
  getDrawableTextureSource,
  readTextureImageData,
} from '@/canvas/components/blockGeometryHelpers';
import type {
  ExtendedMatterBody,
  FallingBlockData,
  IPhysicsEngine,
} from '@/canvas/types/Block';
import type { BlockState } from '@/canvas/types/BlockState';
import { BLOCK_STATE } from '../consts/BLOCK_STATE';

/**
 * 落ちものクラス（PixiJSスプライトとMatter.jsボディの統合）
 */
export class FallingBlock extends Sprite {
  private matterBody: ExtendedMatterBody;
  private blockData: FallingBlockData;
  private state: BlockState;
  private physicsEngine: IPhysicsEngine;

  constructor(
    blockData: FallingBlockData,
    physicsEngine: IPhysicsEngine,
    x: number,
    y: number,
  ) {
    super(blockData.texture);

    this.blockData = blockData;
    this.physicsEngine = physicsEngine;
    this.state = BLOCK_STATE.WAITING;

    this.eventMode = 'none';

    this.anchor.set(0, 0);
    this.pivot.set(blockData.centerOfMassX, blockData.centerOfMassY);
    this.x = x;
    this.y = y;
    this.scale.set(blockData.size);

    this.matterBody = this.createMatterBody(x, y);

    this.matterBody.isStatic = true;
    physicsEngine.addBody(this.matterBody);
  }

  private createMatterBody(x: number, y: number): ExtendedMatterBody {
    const vertices = this.blockData.vertices.map((v) => ({
      x: (v.x - this.blockData.centerOfMassX) * this.blockData.size,
      y: (v.y - this.blockData.centerOfMassY) * this.blockData.size,
    }));

    const body = Matter.Bodies.fromVertices(
      x,
      y,
      [vertices],
      {
        frictionAir: 0.01,
        restitution: 0.3,
        label: `block_${this.blockData.blockId}`,
      },
      false,
    ) as ExtendedMatterBody;

    body.blockId = this.blockData.blockId;
    body.fallingBlock = this;

    return body;
  }

  startFalling(): void {
    if (this.state === BLOCK_STATE.WAITING) {
      this.state = BLOCK_STATE.FALLING;
      this.matterBody.isStatic = false;
    }
  }

  update(): void {
    this.x = this.matterBody.position.x;
    this.y = this.matterBody.position.y;
    this.rotation = this.matterBody.angle;

    if (this.state === BLOCK_STATE.FALLING) {
      const velocity =
        Math.abs(this.matterBody.velocity.x) +
        Math.abs(this.matterBody.velocity.y);
      if (velocity < 0.1) {
        this.state = BLOCK_STATE.RESTING;
      }
    }
  }

  getBlockId(): number {
    return this.blockData.blockId;
  }

  getBlockData(): FallingBlockData {
    return this.blockData;
  }

  getState(): BlockState {
    return this.state;
  }

  getMatterBody(): ExtendedMatterBody {
    return this.matterBody;
  }

  destroy(): void {
    if (this.physicsEngine) {
      this.physicsEngine.removeBody(this.matterBody);
    }
    super.destroy();
  }
}

/**
 * 画像から透過部分を除いた重心とポリゴンを計算するユーティリティ
 */
// biome-ignore lint: ユーティリティクラスとして静的メソッドのみを使用
export class BlockGeometryCalculator {
  static async getVerticesFromSvg(
    svgPath: string,
  ): Promise<Array<Array<{ x: number; y: number }>>> {
    try {
      const response = await fetch(svgPath);
      const svgString = await response.text();

      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

      const pathElements = svgDoc.querySelectorAll('path');

      if (pathElements.length === 0) {
        console.warn(`No path elements found in SVG: ${svgPath}`);
        return [];
      }

      const allVertices: Array<Array<{ x: number; y: number }>> = [];

      pathElements.forEach((pathElement) => {
        const vertices = Matter.Svg.pathToVertices(pathElement, 10);

        if (vertices && vertices.length > 0) {
          allVertices.push(vertices);
        }
      });

      return allVertices;
    } catch (error) {
      console.error(`Failed to load SVG vertices from ${svgPath}:`, error);
      return [];
    }
  }

  static calculateCenterOfMass(texture: Texture): {
    x: number;
    y: number;
  } {
    const context = createTextureCanvasContext(texture);
    if (!context) {
      return { x: 0, y: 0 };
    }

    const { ctx, width, height } = context;
    const source = getDrawableTextureSource(texture);
    if (!source) {
      return { x: width / 2, y: height / 2 };
    }

    ctx.drawImage(source, 0, 0);
    const imageData = readTextureImageData(ctx, width, height);
    const center = calculateOpaqueCenterOfMass(imageData.data, width, height);

    return center ?? { x: width / 2, y: height / 2 };
  }
}
